import { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import { EMERGENCY_TYPES, stationIconSizeForZoom, unitIcon, UNIT_SHORT, UNIT_COLOR } from "@/lib/gameConfig";

const US_CENTER = [39.8283, -98.5795];
const US_BOUNDS = L.latLngBounds([24.396308, -125.0], [49.384358, -66.93457]);
// Default zoom used when no per-user preference is loaded yet.
// 13 ≈ 6-mile viewport, which comfortably frames the 3-mile call-spawn radius.
const DEFAULT_STATION_ZOOM = 13;

// EMERGENCY_TYPES.icon used to hold a single emoji char; we now also support
// PNG/SVG asset paths (e.g. "/icons/fire_icon.png"). Helpers below let the
// existing divIcon HTML keep working for emoji while transparently swapping
// in a full-size <img> tag for the new artwork.
const isIconUrl = (s) =>
  typeof s === "string" && (s.startsWith("/") || s.startsWith("http"));
const renderCallIcon = (icon) => {
  if (isIconUrl(icon)) {
    return `<img src="${icon}" class="call-marker-img-inner" alt="" draggable="false" />`;
  }
  return icon || "";
};

// Truck icons are drawn facing WEST (left). When a dispatch's overall
// route trends predominantly eastward we horizontally flip the image so
// the unit appears to face its direction of travel. North/south-leaning
// routes leave orientation untouched. Heading is computed once per
// dispatch (start → end of routeCoords) for stable, jitter-free flips.
//   → returns " flipped" when the marker img should be mirrored,
//             ""        otherwise.
function flipClassForRoute(coords) {
  if (!Array.isArray(coords) || coords.length < 2) return "";
  const [aLat, aLng] = coords[0];
  const [bLat, bLng] = coords[coords.length - 1];
  const dLat = bLat - aLat;
  const dLng = bLng - aLng;
  // Only flip when E/W movement dominates; bias slightly toward keeping the
  // original orientation when the trip is nearly diagonal.
  if (Math.abs(dLng) <= Math.abs(dLat) * 1.05) return "";
  return dLng > 0 ? " flipped" : "";
}

// ---- CAD-emblem helpers -------------------------------------------------
// Very small rectangle badge with the unit's short code (ENG, MED…) or full
// name, colored by dispatch status. Rendered instead of the truck artwork
// when the player enables Settings → Map Icons → CAD Emblems.
function escapeCadText(s) {
  return String(s || "").replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]
  );
}
// Unit types with light backgrounds need dark text; dark backgrounds need
// light text. Hard-coded per UNIT_COLOR to guarantee accessibility rather
// than compute luminance each render.
const CAD_TEXT_COLOR = {
  "Engine": "#050505",          // red bg, dark text
  "Ladder": "#F8FAFC",          // blue bg, light text
  "Battalion Chief": "#F8FAFC", // purple bg, light text
  "Tanker": "#050505",          // teal bg, dark text
  "Brush Truck": "#050505",     // green bg, dark text
  "Rescue": "#050505",          // yellow bg, dark text
  "Hazmat": "#050505",          // orange bg, dark text
  "Medic": "#F8FAFC",           // pink bg, light text
  "HEMS": "#050505",            // lime bg, dark text
  // Police
  "Patrol Car": "#F8FAFC",      // navy bg → light text
  "Deputy": "#F8FAFC",          // tan bg → light text
  "K9 Unit": "#F8FAFC",         // forest bg → light text
  "Sheriff": "#F8FAFC",         // bronze bg → light text
  "Police Chief": "#050505",    // gold bg → dark text
};
function cadHtml({ apparatusType, apparatusName, returning, showFullName, alliance, scale = 1 }) {
  const label = showFullName
    ? (apparatusName || UNIT_SHORT[apparatusType] || "UNIT")
    : (UNIT_SHORT[apparatusType] || "UNIT");
  const bg = UNIT_COLOR[apparatusType] || "#FFD60A";
  const fg = CAD_TEXT_COLOR[apparatusType] || "#050505";
  const classes = [
    "truck-marker",
    "truck-marker-cad",
    returning ? "returning" : "responding",
  ];
  if (alliance) classes.push("alliance");
  // Background/text colors are unit-type driven (matches the fleet sort
  // filter chips). `.returning` layers a dashed border + slight
  // desaturation. Zoom-aware `scale` shrinks the badge at wider zooms so
  // busy areas stay readable — applied as a CSS transform so we don't
  // have to also swap font sizes.
  const style =
    `background:${bg};color:${fg};` +
    (scale !== 1 ? `transform:scale(${scale});transform-origin:center;` : "");
  return `<div class="${classes.join(" ")}" style="${style}" title="${escapeCadText(apparatusName || apparatusType || "")}">${escapeCadText(label)}</div>`;
}
// Icon width is roughly proportional to text length. Leaflet's iconAnchor
// needs to sit in the visual center so hover + click hit-testing feels
// right. Full-name labels can be quite wide, so cap max-width in CSS AND
// approximate the anchor here so tooltips point at the badge center.
function cadIconSize({ apparatusType, apparatusName, showFullName, scale = 1 }) {
  const text = showFullName
    ? (apparatusName || UNIT_SHORT[apparatusType] || "UNIT")
    : (UNIT_SHORT[apparatusType] || "UNIT");
  // 11px mono → ~7.6px per char + 12px horizontal padding + 3px border.
  const w = Math.min(160, Math.max(34, Math.round(text.length * 7.6) + 15));
  const h = 20;
  return [Math.round(w * scale), Math.round(h * scale)];
}

// Scale the CAD emblem down at wider zoom levels so busy zones stay
// readable. Anchored around the default zoom of 13 (~6-mile viewport).
// At the fully-zoomed-out US view (zoom 3-4) the badge is ~55% size; at
// zoom 12 it's ~85%; at zoom 13+ (street/block) it's full-size 100%.
function cadScaleForZoom(z) {
  if (typeof z !== "number") return 1;
  if (z >= 13) return 1;
  if (z >= 12) return 0.85;
  if (z >= 11) return 0.75;
  if (z >= 9) return 0.65;
  return 0.55;
}

// Below this zoom level, adjacent CAD emblems collapse into a single "+N"
// cluster badge so the US-wide view isn't a wall of overlapping chips.
const CAD_CLUSTER_ZOOM_MAX = 8;
// Approximate pixel bucket for cluster grouping. Buckets are computed via
// `map.latLngToLayerPoint`, so this is a real screen-pixel distance —
// larger = more aggressive merging.
const CAD_CLUSTER_BUCKET_PX = 42;

// HTML for the cluster badge (rounded dark pill showing "+N").
function cadClusterHtml(count) {
  return `<div class="truck-marker truck-marker-cad-cluster" title="${count} units in this area — click to zoom">+${count}</div>`;
}

function MapClickHandler({ handlerRef }) {
  useMapEvents({
    click(e) {
      const fn = handlerRef.current;
      if (fn) fn(e.latlng);
    },
  });
  return null;
}

// Fly to the main station — once after initial load AND every time the user
// picks a different main station OR drags the zoom slider in Settings.
function InitialView({ mainStation, zoom }) {
  const map = useMap();
  const lastFlyRef = useRef({ id: null, zoom: null });
  useEffect(() => {
    if (!mainStation) return;
    const z = zoom ?? DEFAULT_STATION_ZOOM;
    if (lastFlyRef.current.id === mainStation.id && lastFlyRef.current.zoom === z) return;
    const sameStation = lastFlyRef.current.id === mainStation.id;
    lastFlyRef.current = { id: mainStation.id, zoom: z };
    if (sameStation) {
      // Slider drag — just snap-zoom, keep the user's pan position.
      map.setZoom(z, { animate: true });
    } else {
      map.flyTo([mainStation.lat, mainStation.lng], z, { animate: true, duration: 1.2 });
    }
  }, [mainStation, zoom, map]);
  return null;
}

function MapInvalidateOnResize() {
  const map = useMap();
  useEffect(() => {
    if (!map) return;
    const container = map.getContainer();
    if (!container || typeof ResizeObserver === "undefined") return;
    // Debounce slightly to coalesce CSS transition frames.
    let raf = 0;
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        try {
          // animate:false avoids the visible pan glitch when the container width
          // changes by a non-trivial delta (e.g. ~290 px radio collapse).
          map.invalidateSize({ animate: false });
        } catch (_e) { /* map may be torn down */ }
      });
    });
    ro.observe(container);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [map]);
  return null;
}

function MarkersLayer({
  stations,
  hospitals,
  allianceHospitals,
  showAllianceFacilities,
  onHospitalClick,
  calls,
  dispatches,
  onStationClick,
  onCallClick,
  allianceStations,
  showAllianceStations,
  myShares,
  showAllianceUnits,
  sharedCallIds,
  allianceShares,
  currentUserId,
  onAllianceCallClick,
  onAllianceStationClick,
  cadIconsEnabled = true,
  cadIconsShowFullName = false,
}) {
  const map = useMap();
  const layersRef = useRef({
    stations: L.layerGroup(),
    calls: L.layerGroup(),
    trucks: L.layerGroup(),
    routes: L.layerGroup(),
    alliance: L.layerGroup(),    // alliance stations
    alliedUnits: L.layerGroup(), // helpers responding to my shared calls
    allianceCalls: L.layerGroup(), // other players' shared calls (mutual-aid)
    hospitals: L.layerGroup(),   // player + alliance-shared hospitals
    allianceHospitals: L.layerGroup(), // alliance-owned hospitals (toggleable)
  });
  const [iconSize, setIconSize] = useState(() => stationIconSizeForZoom(map.getZoom()));
  // Track raw zoom so CAD emblems shrink at wider zoom levels (kept
  // separate from `iconSize` — that helper is tuned specifically for the
  // fire-station artwork).
  const [mapZoom, setMapZoom] = useState(() => map.getZoom());

  // Track zoom for icon scaling
  useEffect(() => {
    const onZoom = () => {
      setIconSize(stationIconSizeForZoom(map.getZoom()));
      setMapZoom(map.getZoom());
    };
    map.on("zoomend", onZoom);
    return () => {
      map.off("zoomend", onZoom);
    };
  }, [map]);

  useEffect(() => {
    const { stations: sL, calls: cL, trucks: tL, routes: rL, alliance: aL, alliedUnits: auL, allianceCalls: acL, hospitals: hL, allianceHospitals: ahL } = layersRef.current;
    rL.addTo(map);
    sL.addTo(map);
    hL.addTo(map);
    ahL.addTo(map);
    aL.addTo(map);
    acL.addTo(map);
    cL.addTo(map);
    tL.addTo(map);
    auL.addTo(map);
    return () => {
      rL.remove();
      sL.remove();
      hL.remove();
      ahL.remove();
      aL.remove();
      acL.remove();
      cL.remove();
      tL.remove();
      auL.remove();
    };
  }, [map]);

  // Track the latest onHospitalClick in a ref so the hospital-marker effects
  // (player + alliance) can wire click handlers without re-creating markers
  // when the parent passes a new function identity each render.
  const onHospitalClickRef = useRef(onHospitalClick);
  useEffect(() => { onHospitalClickRef.current = onHospitalClick; }, [onHospitalClick]);
  const onAllianceStationClickRef = useRef(onAllianceStationClick);
  useEffect(() => { onAllianceStationClickRef.current = onAllianceStationClick; }, [onAllianceStationClick]);

  // Alliance member stations (toggleable)
  useEffect(() => {
    const layer = layersRef.current.alliance;
    layer.clearLayers();
    if (!showAllianceStations || !allianceStations) return;
    allianceStations.forEach((s) => {
      const iconSrc = s.kind === "police" ? "/police-station.png" : "/fire-station.png";
      const html = `<div class="station-marker alliance" style="width:${Math.max(20, iconSize - 4)}px;height:${Math.max(20, iconSize - 4)}px;filter:hue-rotate(180deg) saturate(0.8) brightness(1.1);cursor:pointer" data-station-id="${s.id}"><img src="${iconSrc}" alt="" draggable="false" /></div>`;
      const icon = L.divIcon({
        className: "",
        html,
        iconSize: [iconSize, iconSize],
        iconAnchor: [iconSize / 2, iconSize / 2],
      });
      const m = L.marker([s.lat, s.lng], { icon, opacity: 0.9 });
      m.bindTooltip(`${s.name} (group)`, {
        direction: "top",
        offset: [0, -iconSize / 2 + 2],
        className: "station-tooltip",
        sticky: false,
      });
      m.on("click", () => onAllianceStationClickRef.current && onAllianceStationClickRef.current(s));
      m.addTo(layer);
    });
  }, [allianceStations, showAllianceStations, iconSize]);

  // Alliance-owned hospitals (toggleable — same flag as alliance stations).
  // Distinguished by a cyan-tint filter to differentiate from player-owned.
  useEffect(() => {
    const layer = layersRef.current.allianceHospitals;
    layer.clearLayers();
    if (!showAllianceFacilities || !allianceHospitals || allianceHospitals.length === 0) return;
    const size = Math.max(24, Math.round((iconSize - 2) * 1.25));
    allianceHospitals.forEach((h) => {
      const beds = h.beds || 0;
      const specs = (h.specialties || []).join(", ") || "none";
      const html = `<div class="hospital-marker alliance" style="width:${size}px;height:${size}px;display:flex;align-items:center;justify-content:center;filter:hue-rotate(160deg) saturate(1.1)" data-hospital-id="${h.id}"><img src="/hospital_icon.png" alt="" draggable="false" style="width:100%;height:100%;object-fit:contain" /></div>`;
      const icon = L.divIcon({
        className: "",
        html,
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2],
      });
      const m = L.marker([h.lat, h.lng], { icon, opacity: 0.95 });
      m.bindTooltip(`${h.name} (group)<br/>${beds} beds · specialties: ${specs}`, {
        direction: "top",
        offset: [0, -size / 2 + 2],
        className: "station-tooltip",
        sticky: false,
      });
      m.on("click", () => onHospitalClickRef.current && onHospitalClickRef.current({ ...h, kind: "alliance" }));
      m.addTo(layer);
    });
  }, [allianceHospitals, showAllianceFacilities, iconSize]);

  // Helper units responding to MY shared calls
  useEffect(() => {
    const layer = layersRef.current.alliedUnits;
    layer.clearLayers();
    if (!showAllianceUnits || !myShares || myShares.length === 0) return;
    const now = Date.now();
    myShares.forEach((s) => {
      (s.helpers || []).forEach((h) => {
        if (h.on_scene) return; // server says arrived
        // Hide as soon as the route would have completed — backend may take
        // up to one poll cycle to confirm `on_scene`. Keeps parity with own
        // trucks which vanish the instant they touch the scene.
        const elapsed = (now - (h.startedAt || now)) / 1000;
        if (elapsed >= (h.durationSec || 1)) return;
        const t = Math.min(1, Math.max(0, elapsed / (h.durationSec || 1)));
        const coords = h.routeCoords || [];
        if (coords.length < 2) return;
        const segCount = coords.length - 1;
        const seg = Math.min(segCount - 1, Math.floor(t * segCount));
        const segT = t * segCount - seg;
        const a = coords[seg];
        const b = coords[seg + 1];
        const lat = a[0] + (b[0] - a[0]) * segT;
        const lng = a[1] + (b[1] - a[1]) * segT;
        const iconUrl = unitIcon(h.apparatusType || "Engine");
        const flipCls = flipClassForRoute(h.routeCoords);
        let icon;
        if (cadIconsEnabled) {
          const cadScale = cadScaleForZoom(mapZoom);
          const [w, hgt] = cadIconSize({
            apparatusType: h.apparatusType,
            apparatusName: h.apparatusName,
            showFullName: cadIconsShowFullName,
            scale: cadScale,
          });
          icon = L.divIcon({
            className: "",
            html: cadHtml({
              apparatusType: h.apparatusType || "Engine",
              apparatusName: h.apparatusName,
              returning: false, // alliance helpers are always outbound
              showFullName: cadIconsShowFullName,
              alliance: true,
              scale: cadScale,
            }),
            iconSize: [w, hgt],
            iconAnchor: [Math.round(w / 2), Math.round(hgt / 2)],
          });
        } else {
          const html = `<div class="truck-marker truck-marker-img alliance${flipCls}"><img src="${iconUrl}" alt="" draggable="false" /></div>`;
          icon = L.divIcon({
            className: "",
            html,
            iconSize: [68, 68],
            iconAnchor: [34, 34],
          });
        }
        const mk = L.marker([lat, lng], { icon, keyboard: false });
        mk.bindTooltip(`${h.apparatusName} (alliance, from ${h.name})`, {
          direction: "top",
          offset: [0, -10],
          className: "station-tooltip",
          sticky: false,
        });
        mk.addTo(layer);
      });
    });
  }, [myShares, showAllianceUnits, cadIconsEnabled, cadIconsShowFullName, mapZoom]);

  // Tick alliance units every 200ms by triggering a setState ref tick
  useEffect(() => {
    if (!showAllianceUnits || !myShares || myShares.length === 0) return;
    const id = setInterval(() => {
      const layer = layersRef.current.alliedUnits;
      const now = Date.now();
      layer.clearLayers();
      myShares.forEach((s) => {
        (s.helpers || []).forEach((h) => {
          if (h.on_scene) return;
          const elapsed = (now - (h.startedAt || now)) / 1000;
          if (elapsed >= (h.durationSec || 1)) return; // arrived client-side, hide
          const t = Math.min(1, Math.max(0, elapsed / (h.durationSec || 1)));
          const coords = h.routeCoords || [];
          if (coords.length < 2) return;
          const segCount = coords.length - 1;
          const seg = Math.min(segCount - 1, Math.floor(t * segCount));
          const segT = t * segCount - seg;
          const a = coords[seg];
          const b = coords[seg + 1];
          const lat = a[0] + (b[0] - a[0]) * segT;
          const lng = a[1] + (b[1] - a[1]) * segT;
          const iconUrl = unitIcon(h.apparatusType || "Engine");
          const flipCls = flipClassForRoute(h.routeCoords);
          let icon;
          if (cadIconsEnabled) {
            const cadScale = cadScaleForZoom(mapZoom);
            const [w, hgt] = cadIconSize({
              apparatusType: h.apparatusType,
              apparatusName: h.apparatusName,
              showFullName: cadIconsShowFullName,
              scale: cadScale,
            });
            icon = L.divIcon({
              className: "",
              html: cadHtml({
                apparatusType: h.apparatusType || "Engine",
                apparatusName: h.apparatusName,
                returning: false,
                showFullName: cadIconsShowFullName,
                alliance: true,
                scale: cadScale,
              }),
              iconSize: [w, hgt],
              iconAnchor: [Math.round(w / 2), Math.round(hgt / 2)],
            });
          } else {
            const html = `<div class="truck-marker truck-marker-img alliance${flipCls}"><img src="${iconUrl}" alt="" draggable="false" /></div>`;
            icon = L.divIcon({
              className: "",
              html,
              iconSize: [68, 68],
              iconAnchor: [34, 34],
            });
          }
          const mk = L.marker([lat, lng], { icon, keyboard: false });
          mk.bindTooltip(`${h.apparatusName} (alliance, from ${h.name})`, {
            direction: "top",
            offset: [0, -10],
            className: "station-tooltip",
            sticky: false,
          });
          mk.addTo(layer);
        });
      });
    }, 200);
    return () => clearInterval(id);
  }, [myShares, showAllianceUnits, cadIconsEnabled, cadIconsShowFullName, mapZoom]);

  // Other players' shared calls (mutual-aid) — stable marker pattern so the
  // 3-second "newly shared" flash only fires once when the share first
  // appears, not on every 3-second alliance poll.
  const allianceMarkersRef = useRef(new Map()); // shareId -> { marker, signature }
  const onAllianceCallClickRef = useRef(onAllianceCallClick);
  useEffect(() => { onAllianceCallClickRef.current = onAllianceCallClick; }, [onAllianceCallClick]);
  useEffect(() => {
    const layer = layersRef.current.allianceCalls;
    const live = new Set();
    (allianceShares || []).forEach((s) => {
      if (!s?.call) return;
      if (s.requester_id === currentUserId) return; // skip my own shares
      const t = EMERGENCY_TYPES[s.call.type];
      if (!t) return;
      live.add(s.id);
      const isImg = isIconUrl(t.icon);
      const sig = `${s.call.type}|${isImg ? 1 : 0}`;
      const wrapperCls = isImg
        ? `call-marker call-marker-img call-marker-shared alliance ${s.call.type}`
        : `call-marker call-marker-shared alliance ${s.call.type}`;
      const entry = allianceMarkersRef.current.get(s.id);
      if (!entry) {
        const icon = L.divIcon({
          className: "",
          html: `<div class="${wrapperCls}">${renderCallIcon(t.icon)}</div>`,
          iconSize: isImg ? [54, 66] : [26, 26],
          iconAnchor: isImg ? [27, 60] : [13, 13],
        });
        const m = L.marker([s.call.lat, s.call.lng], { icon })
          .bindTooltip(
            `${t.label} — mutual aid from ${s.requester_name} (click to view)`,
            { direction: "top", offset: [0, -10] }
          );
        m.on("click", () => onAllianceCallClickRef.current && onAllianceCallClickRef.current(s));
        m.addTo(layer);
        allianceMarkersRef.current.set(s.id, { marker: m, signature: sig });
        return;
      }
      // Re-render icon only on real visual change (call type upgraded).
      if (entry.signature !== sig) {
        const icon = L.divIcon({
          className: "",
          html: `<div class="${wrapperCls}">${renderCallIcon(t.icon)}</div>`,
          iconSize: isImg ? [54, 66] : [26, 26],
          iconAnchor: isImg ? [27, 60] : [13, 13],
        });
        entry.marker.setIcon(icon);
        entry.marker.setTooltipContent(
          `${t.label} — mutual aid from ${s.requester_name} (click to view)`
        );
        entry.signature = sig;
      }
    });
    // Sweep markers for shares that have closed/resolved.
    allianceMarkersRef.current.forEach((entry, id) => {
      if (!live.has(id)) {
        layer.removeLayer(entry.marker);
        allianceMarkersRef.current.delete(id);
      }
    });
  }, [allianceShares, currentUserId]);

  // Stations — fire station logo image; hover shows name; zoom-aware size
  useEffect(() => {
    const layer = layersRef.current.stations;
    layer.clearLayers();
    stations.forEach((s) => {
      const iconSrc = s.kind === "police" ? "/police-station.png" : "/fire-station.png";
      const html = `<div class="station-marker" style="width:${iconSize}px;height:${iconSize}px" data-station-id="${s.id}"><img src="${iconSrc}" alt="" draggable="false" /></div>`;
      const icon = L.divIcon({
        className: "",
        html,
        iconSize: [iconSize, iconSize],
        iconAnchor: [iconSize / 2, iconSize / 2],
      });
      const m = L.marker([s.lat, s.lng], { icon });
      m.bindTooltip(s.name, {
        direction: "top",
        offset: [0, -iconSize / 2 + 2],
        className: "station-tooltip",
        sticky: false,
      });
      m.on("click", () => onStationClick && onStationClick(s));
      m.addTo(layer);
    });
  }, [stations, onStationClick, iconSize]);

  // Hospitals — illustrated icon (replaces old red "H" badge). Uses the
  // stable React-ref-map pattern so the marker doesn't get torn down and
  // re-created on every state tick (caused visible flashing). We only:
  //   • add a marker when a new hospital appears
  //   • remove it when the hospital is gone
  //   • move it with setLatLng if the location changed
  //   • re-render the tooltip when beds/specialties changed
  const hospitalMarkersRef = useRef(new Map()); // hid -> { marker, lat, lng, signature, size }
  useEffect(() => {
    const layer = layersRef.current.hospitals;
    // 25% larger than the fire-station baseline.
    const size = Math.max(24, Math.round((iconSize - 2) * 1.25));
    const live = new Set();
    (hospitals || []).forEach((h) => {
      live.add(h.id);
      const beds = h.beds || 0;
      const specs = (h.specialties || []).join(", ") || "none";
      const tooltip = `${h.name}<br/>${beds} beds · specialties: ${specs}`;
      const entry = hospitalMarkersRef.current.get(h.id);
      if (!entry) {
        const html = `<div class="hospital-marker" style="width:${size}px;height:${size}px;display:flex;align-items:center;justify-content:center" data-hospital-id="${h.id}"><img src="/hospital_icon.png" alt="" draggable="false" style="width:100%;height:100%;object-fit:contain" /></div>`;
        const icon = L.divIcon({
          className: "",
          html,
          iconSize: [size, size],
          iconAnchor: [size / 2, size / 2],
        });
        const m = L.marker([h.lat, h.lng], { icon });
        m.bindTooltip(tooltip, {
          direction: "top",
          offset: [0, -size / 2 + 2],
          className: "station-tooltip",
          sticky: false,
        });
        m.on("click", () => onHospitalClickRef.current && onHospitalClickRef.current(h));
        m.addTo(layer);
        hospitalMarkersRef.current.set(h.id, { marker: m, lat: h.lat, lng: h.lng, tooltip, size });
        return;
      }
      // Existing marker — only mutate the parts that changed.
      if (entry.lat !== h.lat || entry.lng !== h.lng) {
        entry.marker.setLatLng([h.lat, h.lng]);
        entry.lat = h.lat;
        entry.lng = h.lng;
      }
      if (entry.size !== size) {
        const html = `<div class="hospital-marker" style="width:${size}px;height:${size}px;display:flex;align-items:center;justify-content:center" data-hospital-id="${h.id}"><img src="/hospital_icon.png" alt="" draggable="false" style="width:100%;height:100%;object-fit:contain" /></div>`;
        entry.marker.setIcon(L.divIcon({
          className: "",
          html,
          iconSize: [size, size],
          iconAnchor: [size / 2, size / 2],
        }));
        entry.size = size;
      }
      if (entry.tooltip !== tooltip) {
        entry.marker.setTooltipContent(tooltip);
        entry.tooltip = tooltip;
      }
    });
    // Sweep markers whose hospital is gone.
    hospitalMarkersRef.current.forEach((entry, id) => {
      if (!live.has(id)) {
        layer.removeLayer(entry.marker);
        hospitalMarkersRef.current.delete(id);
      }
    });
  }, [hospitals, iconSize]);

  // Calls — KEEP marker instances across renders (keyed by call.id) so the
  // 100 ms engine tick doesn't tear down and re-create every pin (which
  // produced the visible flashing). We only:
  //   • add a marker when a new callId appears
  //   • remove it when the callId is gone
  //   • re-render the divIcon HTML when shared-status changes (rare)
  const callMarkersRef = useRef(new Map()); // callId -> { marker, signature }
  const onCallClickRef = useRef(onCallClick);
  useEffect(() => { onCallClickRef.current = onCallClick; }, [onCallClick]);
  useEffect(() => {
    const layer = layersRef.current.calls;
    const live = new Set();
    calls.forEach((c) => {
      const t = EMERGENCY_TYPES[c.type];
      if (!t) return;
      live.add(c.id);
      const isShared = !!(sharedCallIds && sharedCallIds.has(c.id));
      const isImg = isIconUrl(t.icon);
      // Signature: anything that affects the divIcon's HTML/size. If it
      // matches what we already drew, skip the divIcon swap entirely.
      const sig = `${c.type}|${isShared ? 1 : 0}|${isImg ? 1 : 0}`;
      const entry = callMarkersRef.current.get(c.id);
      if (!entry) {
        const wrapperCls = isImg
          ? `call-marker call-marker-img ${c.type}${isShared ? " call-marker-shared" : ""}`
          : `call-marker ${c.type}${isShared ? " call-marker-shared" : ""}`;
        const icon = L.divIcon({
          className: "",
          html: `<div class="${wrapperCls}">${renderCallIcon(t.icon)}</div>`,
          iconSize: isImg ? [54, 66] : [26, 26],
          iconAnchor: isImg ? [27, 60] : [13, 13],
        });
        const m = L.marker([c.lat, c.lng], { icon })
          .bindTooltip(`${t.label} — click for details`, { direction: "top", offset: [0, -10] });
        m.on("click", () => onCallClickRef.current && onCallClickRef.current(c));
        m.addTo(layer);
        callMarkersRef.current.set(c.id, { marker: m, signature: sig });
        return;
      }
      // Existing marker — only swap the icon if shared/type actually changed.
      if (entry.signature !== sig) {
        const wrapperCls = isImg
          ? `call-marker call-marker-img ${c.type}${isShared ? " call-marker-shared" : ""}`
          : `call-marker ${c.type}${isShared ? " call-marker-shared" : ""}`;
        const icon = L.divIcon({
          className: "",
          html: `<div class="${wrapperCls}">${renderCallIcon(t.icon)}</div>`,
          iconSize: isImg ? [54, 66] : [26, 26],
          iconAnchor: isImg ? [27, 60] : [13, 13],
        });
        entry.marker.setIcon(icon);
        entry.marker.setTooltipContent(`${t.label} — click for details`);
        entry.signature = sig;
      }
    });
    // Sweep markers whose call has gone away.
    callMarkersRef.current.forEach((entry, id) => {
      if (!live.has(id)) {
        layer.removeLayer(entry.marker);
        callMarkersRef.current.delete(id);
      }
    });
  }, [calls, sharedCallIds]);

  // Trucks (no response path line) — hover shows the unit's name; click opens parent station.
  // We KEEP marker instances across renders (keyed by dispatch.id) and only move them with
  // setLatLng. Re-creating L.marker every 100 ms used to race the user's click and drop it.
  const truckMarkersRef = useRef(new Map()); // dispatchId -> { marker, lastLabel, lastSignature }
  // Cluster badges shown when CAD emblems are enabled AND the zoom is
  // <= CAD_CLUSTER_ZOOM_MAX. Keyed by pixel bucket ("bx:by"); marker
  // instances are reused across ticks when count + centroid don't move.
  const clusterMarkersRef = useRef(new Map()); // key -> { marker, lastSig }
  const stationsRef = useRef(stations);
  const onStationClickRef = useRef(onStationClick);
  useEffect(() => {
    stationsRef.current = stations;
  }, [stations]);
  useEffect(() => {
    onStationClickRef.current = onStationClick;
  }, [onStationClick]);

  useEffect(() => {
    const tLayer = layersRef.current.trucks;
    const rLayer = layersRef.current.routes;
    rLayer.clearLayers();

    const cadOn = !!cadIconsEnabled;
    const cadFull = !!cadIconsShowFullName;
    const cadScale = cadScaleForZoom(mapZoom);
    const shouldCluster = cadOn && typeof mapZoom === "number" && mapZoom <= CAD_CLUSTER_ZOOM_MAX;

    // Active dispatch list (post-filter). Same rules apply to both the
    // individual and clustered code paths.
    const active = dispatches.filter((d) => !d.onScene && !d.queued && !d.unloading);

    // Bucket dispatches by pixel coordinates when clustering. Buckets of
    // size >= 2 render a cluster badge; buckets of size 1 fall back to
    // the individual marker path so a lone unit still shows its unit-type
    // color.
    let clusteredIds = new Set();
    const clustersToRender = []; // { key, lat, lng, count }
    if (shouldCluster) {
      const buckets = new Map();
      active.forEach((d) => {
        const p = map.latLngToLayerPoint([d.lat, d.lng]);
        const bx = Math.floor(p.x / CAD_CLUSTER_BUCKET_PX);
        const by = Math.floor(p.y / CAD_CLUSTER_BUCKET_PX);
        const key = `${bx}:${by}`;
        if (!buckets.has(key)) buckets.set(key, { latSum: 0, lngSum: 0, ids: [] });
        const b = buckets.get(key);
        b.latSum += d.lat;
        b.lngSum += d.lng;
        b.ids.push(d.id);
      });
      buckets.forEach((b, key) => {
        if (b.ids.length >= 2) {
          clustersToRender.push({
            key,
            lat: b.latSum / b.ids.length,
            lng: b.lngSum / b.ids.length,
            count: b.ids.length,
          });
          b.ids.forEach((id) => clusteredIds.add(id));
        }
      });
    }

    // ---- Individual truck markers -------------------------------------
    const seenIds = new Set();
    active.forEach((d) => {
      if (clusteredIds.has(d.id)) return; // rendered as part of a cluster
      seenIds.add(d.id);
      const label = d.returning
        ? `${d.apparatusName} — returning · click for station`
        : `${d.apparatusName} — responding · click for station`;
      const iconUrl = unitIcon(d.apparatusType || "Engine");
      const flipCls = flipClassForRoute(d.routeCoords);
      // ---- CAD vs image icon ----
      const signature = cadOn
        ? `cad:${cadFull ? 1 : 0}:${cadScale}:${d.apparatusType || "Engine"}:${d.apparatusName || ""}:${d.returning ? 1 : 0}`
        : `img:${iconUrl}${flipCls}`;
      const buildIcon = () => {
        if (cadOn) {
          const [w, h] = cadIconSize({
            apparatusType: d.apparatusType,
            apparatusName: d.apparatusName,
            showFullName: cadFull,
            scale: cadScale,
          });
          return L.divIcon({
            className: "",
            html: cadHtml({
              apparatusType: d.apparatusType || "Engine",
              apparatusName: d.apparatusName,
              returning: d.returning,
              showFullName: cadFull,
              alliance: false,
              scale: cadScale,
            }),
            iconSize: [w, h],
            iconAnchor: [Math.round(w / 2), Math.round(h / 2)],
          });
        }
        return L.divIcon({
          className: "",
          html: `<div class="truck-marker truck-marker-img${flipCls}"><img src="${iconUrl}" alt="" draggable="false" /></div>`,
          iconSize: [68, 68],
          iconAnchor: [34, 34],
        });
      };
      let entry = truckMarkersRef.current.get(d.id);
      if (!entry) {
        const truckIcon = buildIcon();
        const m = L.marker([d.lat, d.lng], { icon: truckIcon, keyboard: false })
          .bindTooltip(label, {
            direction: "top",
            offset: [0, -10],
            className: "station-tooltip",
            sticky: false,
          });
        const sid = d.stationId;
        m.on("click", (e) => {
          if (e?.originalEvent) L.DomEvent.stopPropagation(e.originalEvent);
          const parent = (stationsRef.current || []).find((s) => s.id === sid);
          const fn = onStationClickRef.current;
          if (parent && fn) fn(parent);
        });
        m.addTo(tLayer);
        entry = { marker: m, lastLabel: label, lastSignature: signature };
        truckMarkersRef.current.set(d.id, entry);
      } else {
        entry.marker.setLatLng([d.lat, d.lng]);
        if (entry.lastLabel !== label) {
          entry.marker.setTooltipContent(label);
          entry.lastLabel = label;
        }
        if (entry.lastSignature !== signature) {
          entry.marker.setIcon(buildIcon());
          entry.lastSignature = signature;
        }
      }
    });

    // Remove markers for dispatches that no longer exist, went on-scene,
    // or are now folded into a cluster.
    truckMarkersRef.current.forEach((entry, id) => {
      if (!seenIds.has(id)) {
        tLayer.removeLayer(entry.marker);
        truckMarkersRef.current.delete(id);
      }
    });

    // ---- Cluster badges -----------------------------------------------
    const seenClusterKeys = new Set();
    clustersToRender.forEach((c) => {
      seenClusterKeys.add(c.key);
      // Signature: count + coarse centroid (0.1° buckets → ~7-11 mi at
      // mid-US latitudes). Coarse rounding avoids re-icon churn on every
      // 100 ms tick as units nudge a few pixels.
      const sig = `${c.count}:${c.lat.toFixed(1)}:${c.lng.toFixed(1)}`;
      let entry = clusterMarkersRef.current.get(c.key);
      const buildClusterIcon = () =>
        L.divIcon({
          className: "",
          html: cadClusterHtml(c.count),
          iconSize: [40, 22],
          iconAnchor: [20, 11],
        });
      if (!entry) {
        const m = L.marker([c.lat, c.lng], { icon: buildClusterIcon(), keyboard: false })
          .bindTooltip(`${c.count} units in this area — click to zoom`, {
            direction: "top",
            offset: [0, -10],
            className: "station-tooltip",
            sticky: false,
          });
        m.on("click", (e) => {
          if (e?.originalEvent) L.DomEvent.stopPropagation(e.originalEvent);
          // Zoom in enough to break the bucket apart on next render.
          const target = Math.min(17, map.getZoom() + 3);
          map.flyTo([c.lat, c.lng], target, { animate: true, duration: 0.8 });
        });
        m.addTo(tLayer);
        entry = { marker: m, lastSig: sig };
        clusterMarkersRef.current.set(c.key, entry);
      } else {
        entry.marker.setLatLng([c.lat, c.lng]);
        if (entry.lastSig !== sig) {
          entry.marker.setIcon(buildClusterIcon());
          entry.marker.setTooltipContent(`${c.count} units in this area — click to zoom`);
          entry.lastSig = sig;
        }
      }
    });
    // Sweep cluster markers whose bucket no longer exists.
    clusterMarkersRef.current.forEach((entry, key) => {
      if (!seenClusterKeys.has(key)) {
        tLayer.removeLayer(entry.marker);
        clusterMarkersRef.current.delete(key);
      }
    });
  }, [dispatches, cadIconsEnabled, cadIconsShowFullName, mapZoom, map]);

  return null;
}

function escapeHtml(s) {
  return (s || "").replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]
  );
}

export default function MapView({
  stations,
  hospitals,
  allianceHospitals,
  showAllianceFacilities,
  onHospitalClick,
  calls,
  dispatches,
  onMapClick,
  onStationClick,
  onCallClick,
  placeMode,
  mainStation,
  mainStationZoom,
  allianceStations,
  showAllianceStations,
  myShares,
  showAllianceUnits,
  sharedCallIds,
  allianceShares,
  currentUserId,
  onAllianceCallClick,
  onAllianceStationClick,
  cadIconsEnabled = true,
  cadIconsShowFullName = false,
}) {
  const handlerRef = useRef(null);
  handlerRef.current = placeMode ? onMapClick : null;

  const containerStyle = useMemo(
    () => ({ cursor: placeMode ? "crosshair" : "" }),
    [placeMode]
  );

  return (
    <div data-testid="map-container" className="w-full h-full">
    <MapContainer
      center={US_CENTER}
      zoom={4}
      minZoom={3}
      maxZoom={17}
      maxBounds={US_BOUNDS.pad(0.5)}
      zoomControl={true}
      attributionControl={true}
      worldCopyJump={false}
      preferCanvas
      style={{ width: "100%", height: "100%", ...containerStyle }}
    >
      <TileLayer
        url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        maxZoom={19}
      />
      <InitialView mainStation={mainStation} zoom={mainStationZoom} />
      <MapClickHandler handlerRef={handlerRef} />
      <MapInvalidateOnResize />
      <MarkersLayer
        stations={stations}
        hospitals={hospitals}
        allianceHospitals={allianceHospitals}
        showAllianceFacilities={showAllianceFacilities}
        onHospitalClick={onHospitalClick}
        calls={calls}
        dispatches={dispatches}
        onStationClick={onStationClick}
        onCallClick={onCallClick}
        allianceStations={allianceStations}
        showAllianceStations={showAllianceStations}
        myShares={myShares}
        showAllianceUnits={showAllianceUnits}
        sharedCallIds={sharedCallIds}
        allianceShares={allianceShares}
        currentUserId={currentUserId}
        onAllianceCallClick={onAllianceCallClick}
        onAllianceStationClick={onAllianceStationClick}
        cadIconsEnabled={cadIconsEnabled}
        cadIconsShowFullName={cadIconsShowFullName}
      />
    </MapContainer>
    </div>
  );
}
